import React, { useState, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { Services } from '@/services/services';
import { Translates } from '@/services/repository/i18n';
import { ControlledDropdown } from '@/components/forms/common/CustomDropdown';

export interface FormBase {
  _general: string;
}

export interface RegistrationFormData extends FormBase {
  lang: string
  username: string
  email: string
  phoneNumber: string
  password: string
  passwordRepeat: string
}

export interface FormError {
  message: string;
}

export type FormErrors<T> = {
  [K in keyof T]: FormError;
}

export const RegistrationForm = ({
  services,
  onSubmit,
  serverErrors,
  form,
}: {
  services: Services;
  onSubmit: (
    data: RegistrationFormData,
    resetForm: () => void
  ) => Promise<void>;
  serverErrors: FormErrors<RegistrationFormData> | undefined;
  form: UseFormReturn<RegistrationFormData>;
}) => {
  const [translates, setTranslates] = useState<Translates>();
  const {
    setFocus,
    register,
    setError,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = form;

  useEffect(() => {
    services.i18n.get('main').then(setTranslates);
  }, []);

  useEffect(() => {
    if (serverErrors === undefined) {
      return;
    }
    for (let i in serverErrors) {
      const key = i as keyof RegistrationFormData;
      setError(key, {
        type: 'server',
        message: translates?.for(serverErrors[key]!.message),
      });
      setFocus(key);
    }
  }, [translates, serverErrors]);

  const handleFormSubmit: SubmitHandler<RegistrationFormData> = (data) => {
    if (data.password !== data.passwordRepeat) {
      setError('passwordRepeat', {
        type: 'validate',
        message: translates?.for('forms.registration.errors.passwordMismatch'),
      });
      return;
    }
    onSubmit(data, reset);
  };

  if (translates === undefined) {
    return <div className='spinner'></div>
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>

      {/* Email Field */}
      <div className="form-field">
        <label htmlFor="email">{translates.for('forms.registration.fields.email.title')}:</label>
        <input
          type="email"
          id="email"
          readOnly={true}
          placeholder={translates.for('forms.registration.fields.email.placeholder')}
          {...register('email', {
            required: translates.for('forms.registration.errors.fields.email.required'),
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              message: translates.for('forms.registration.errors.fields.email.invalid'),
            },
          })}
        />
        {errors.email && <p className="errorMessage">{errors.email.message}</p>}
      </div>

      {/* Language Field */}
      <div className="form-field">
        <label htmlFor="lang">{translates.for('forms.verifyemail.fields.lang.title')}:</label>
        <div className='custom-select'>
          {/* <select
            id="lang"
            {...register('lang', {
              required: translates.for('forms.verifyemail.errors.fields.lang.required'),
            })}
          >
            {services.i18n.languages().map((lang) => <option value={lang} selected={getValues().lang === lang}>{translates.for('dicts.langs.' + lang)}</option>)}
          </select> */}

          <ControlledDropdown
            name='lang'
            control={form.control}
            options={services.i18n.languages().map((lang) => ({
              value: lang,
              label: translates.for('dicts.langs.' + lang)
            })) || []}
            rules={{
                required: translates.for('forms.verifyemail.errors.fields.lang.required'),
            }}
          />
        </div>
        {errors.lang && <p className="errorMessage">{errors.lang.message}</p>}
      </div>

      {/* Username Field */}
      <div className="form-field">
        <label htmlFor="username">{translates.for('forms.registration.fields.username.title')}:</label>
        <input
          type="text"
          id="username"
          placeholder={translates.for('forms.registration.fields.username.placeholder')}
          {...register('username', {
            required: translates.for('forms.registration.errors.fields.username.required'),
          })}
        />
        {errors.username && <p className="errorMessage">{errors.username.message}</p>}
      </div>

      {/* Username Field */}
      <div className="form-field">
        <label htmlFor="phoneNumber">{translates.for('forms.registration.fields.phoneNumber.title')}:</label>
        <input
          type="text"
          id="phoneNumber"
          placeholder={translates.for('forms.registration.fields.username.placeholder')}
          {...register('phoneNumber', {
            required: translates.for('forms.registration.errors.fields.username.required'),
          })}
        />
        {errors.phoneNumber && <p className="errorMessage">{errors.phoneNumber.message}</p>}
      </div>

      {/* Login Field */}
      <div className="form-field">
        <label htmlFor="password">{translates.for('forms.registration.fields.password.title')}:</label>
        <input
          type="password"
          id="password"
          placeholder={translates.for('forms.registration.fields.password.placeholder')}
          {...register('password', {
            required: translates.for('forms.registration.errors.fields.password.required'),
          })}
        />
        {errors.password && <p className="errorMessage">{errors.password.message}</p>}
      </div>

      {/* Login Repeat Field */}
      <div className="form-field">
        <label htmlFor="passwordRepeat">{translates.for('forms.registration.fields.passwordRepeat.title')}:</label>
        <input
          type="password"
          id="passwordRepeat"
          placeholder={translates.for('forms.registration.fields.passwordRepeat.placeholder')}
          {...register('passwordRepeat', {
            required: translates.for('forms.registration.errors.fields.passwordRepeat.required'),
          })}
        />
        {errors.passwordRepeat && <p className="errorMessage">{errors.passwordRepeat.message}</p>}
      </div>

      <button className="submit-button" type="submit">
        {translates.for('forms.registration.submit')}
      </button>
      {errors._general && <p className="errorMessage">{errors._general.message}</p>}
    </form>
  )
}